import { useState } from 'react';
import { adminApi } from '@/api/endpoints';
import type { User, UserStatus } from '@/api/types';
import { Alert, Button, Card, Notice, Spinner } from '@/components/ui';
import { useAsync } from '@/lib/useAsync';
import styles from './admin.module.css';

const TABS: { status: UserStatus; label: string }[] = [
  { status: 'pending', label: 'Wartend' },
  { status: 'active', label: 'Aktiv' },
  { status: 'disabled', label: 'Gesperrt' },
];

function statusBadgeClass(status?: UserStatus): string {
  switch (status) {
    case 'active':
      return `${styles.statusBadge} ${styles.statusActive}`;
    case 'disabled':
      return `${styles.statusBadge} ${styles.statusDisabled}`;
    default:
      return `${styles.statusBadge} ${styles.statusPending}`;
  }
}

export function AdminUsersPage() {
  const [status, setStatus] = useState<UserStatus>('pending');
  const { data, loading, error, reload } = useAsync<User[]>(
    (signal) => adminApi.listUsers(status, signal),
    [status],
  );

  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /** Zuletzt erzeugtes Passwort — nur hier einmalig sichtbar, wird nicht gespeichert. */
  const [resetResult, setResetResult] = useState<{ name: string; password: string } | null>(null);

  async function run(id: number, fn: () => Promise<unknown>) {
    setBusyId(id);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Aktion fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  }

  async function resetPassword(user: User) {
    if (
      !window.confirm(
        `Passwort von ${user.display_name} zurücksetzen?\n\n` +
          'Das bisherige Passwort wird sofort ungültig. Du bekommst ein neues ' +
          'angezeigt und musst es weitergeben.',
      )
    ) {
      return;
    }
    setBusyId(user.id);
    setActionError(null);
    setResetResult(null);
    try {
      const password = await adminApi.resetUserPassword(user.id);
      setResetResult({ name: user.display_name, password });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Zurücksetzen fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <nav className={styles.subnav}>
        {TABS.map((t) => (
          <button
            key={t.status}
            type="button"
            className={
              status === t.status
                ? `${styles.subnavLink} ${styles.subnavLinkActive}`
                : styles.subnavLink
            }
            onClick={() => setStatus(t.status)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {actionError && <Alert>{actionError}</Alert>}

      {resetResult && (
        <Notice>
          Neues Passwort für <strong>{resetResult.name}</strong>:{' '}
          <code className={styles.tempPassword}>{resetResult.password}</code>
          <br />
          Jetzt notieren und weitergeben — es wird nur dieses eine Mal angezeigt. Das Mitglied
          sollte es unter „Mein Profil" sofort ändern.
        </Notice>
      )}

      {loading && <Spinner label="Nutzer werden geladen…" />}
      {error && <Alert>{error}</Alert>}

      {!loading && !error && (data?.length ?? 0) === 0 && (
        <Card>
          <p className="muted">Keine Nutzer mit diesem Status.</p>
        </Card>
      )}

      {!loading &&
        !error &&
        data?.map((u) => (
          <Card key={u.id}>
            <div className={styles.row}>
              <div className={styles.rowMain}>
                <div className={styles.rowTitle}>{u.display_name}</div>
                <div className="muted">{u.email}</div>
                <div className={styles.counts}>
                  <span className={statusBadgeClass(u.status ?? status)}>{u.status ?? status}</span>
                </div>
              </div>
              <div className={styles.actions}>
                {status !== 'active' && (
                  <Button
                    className={styles.btnSm}
                    disabled={busyId === u.id}
                    onClick={() => void run(u.id, () => adminApi.approveUser(u.id))}
                  >
                    Freischalten
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className={styles.btnSm}
                  disabled={busyId === u.id}
                  onClick={() => void resetPassword(u)}
                >
                  Passwort zurücksetzen
                </Button>
                {status !== 'disabled' && (
                  <Button
                    className={`${styles.btnSm} ${styles.btnDanger}`}
                    disabled={busyId === u.id}
                    onClick={() => void run(u.id, () => adminApi.disableUser(u.id))}
                  >
                    Sperren
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
    </>
  );
}
